import { type ComponentPropsWithoutRef, useMemo } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { type ClassValue } from 'clsx';
import { useTranslation } from 'next-i18next';
import { MinusSmallIcon, PlusSmallIcon } from '@/icons';
import { usePublicCategories, usePublicCategoryFilter, usePublicSubjectFilterSelection } from '@/module/public/hooks';
import { useFindSubjectGroupCount } from '@/module/public/hooks/useFindSubjectGroupCount';
import { Checkbox, RadioGroupItem } from '@/ui';
import { Label } from '@/ui/components/label';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/ui_v3/components/accordion';
import { cn } from '@/utils/tailwind.util';
import { FilterAccordionId, useAccordionStorage } from '../../store/AccordionStore';
import { FilterMenuItem } from './AsideFilter';
const SubjectGroupCount = ({
  categoryGroupId,
  isSubSubject
}: {
  categoryGroupId: string;
  isSubSubject: boolean;
}) => {
  const findSubjectGroupCount = useFindSubjectGroupCount();
  return <span className={cn('text-caption-lg-medium', 'text-assistive')} data-sentry-component="SubjectGroupCount" data-sentry-source-file="SubjectAccordion.tsx">
      {findSubjectGroupCount(categoryGroupId, isSubSubject)}
    </span>;
};
export const SubjectAccordionItems = () => {
  const {
    t
  } = useTranslation(['common']);
  return <AccordionItem value={FilterAccordionId.Subject} data-sentry-element="AccordionItem" data-sentry-component="SubjectAccordionItems" data-sentry-source-file="SubjectAccordion.tsx">
      <AccordionTrigger data-sentry-element="AccordionTrigger" data-sentry-source-file="SubjectAccordion.tsx">{t('category.subject')}</AccordionTrigger>
      <AccordionContent className={'p-none'} data-sentry-element="AccordionContent" data-sentry-source-file="SubjectAccordion.tsx">
        <SubjectAccordionItemsComponent data-sentry-element="SubjectAccordionItemsComponent" data-sentry-source-file="SubjectAccordion.tsx" />
      </AccordionContent>
    </AccordionItem>;
};
export const SubjectAccordionItemsComponent = () => {
  const {
    subjectGroups
  } = usePublicCategories();
  const {
    expandedItems
  } = useAccordionStorage();
  const {
    selectedSubject,
    isSubSubjectSelected
  } = usePublicCategoryFilter();
  const {
    selectSubjectFilter
  } = usePublicSubjectFilterSelection();
  const getSelectedStatusColor = (selected: boolean): ClassValue[] => {
    return selected ? ['text-strong hover:text-violet-400', 'text-body-md-bold'] : ['hover:text-alternative', 'text-body-md-semibold', 'text-default'];
  };
  const subjectGroupsArr = useMemo(() => {
    return Object.entries(subjectGroups).sort((a, b) => {
      return a[1].order - b[1].order;
    });
  }, [subjectGroups]);
  return subjectGroupsArr.map(([mainSubjectGroupId, mainSubject]) => {
    const hasOwnSubSubjects = Object.keys(mainSubject.children).some(childGroupId => childGroupId !== mainSubjectGroupId);
    if (!hasOwnSubSubjects) {
      return <FilterMenuItem onClick={() => {
        selectSubjectFilter({
          subjectId: mainSubjectGroupId,
          subjectGroupId: mainSubjectGroupId,
          shouldExpandSubjectGroup: false
        });
      }} className={cn('text-body-sm-semibold h-[44px] gap-xs flex pl-lg', getSelectedStatusColor(selectedSubject === mainSubjectGroupId))} key={mainSubjectGroupId}>
          {mainSubject.name}
          <SubjectGroupCount categoryGroupId={mainSubjectGroupId} isSubSubject={false} />
        </FilterMenuItem>;
    }
    const mainSubjectAccordionValue = FilterAccordionId.SubjectGroup + mainSubjectGroupId;
    const isSelectedMainSubject = expandedItems.includes(mainSubjectAccordionValue) && !isSubSubjectSelected;
    return <AccordionItem className={'pl-none'} key={mainSubjectGroupId} value={mainSubjectAccordionValue}>
        <AccordionTrigger className={cn(['pl-lg flex items-center gap-xs'], getSelectedStatusColor(isSelectedMainSubject))} icon={isSelectedMainSubject ? <MinusSmallIcon fill={ICON_COLOR.alternative} size={'sm'} className={'shrink-0'} /> : <PlusSmallIcon fill={ICON_COLOR.alternative} size={'sm'} className={'shrink-0'} />}>
          <div data-id={mainSubject.groupId} className={'flex size-full items-center gap-xs'}>
            {mainSubject.name}
            <SubjectGroupCount categoryGroupId={mainSubjectGroupId} isSubSubject={false} />
          </div>
        </AccordionTrigger>
        {hasOwnSubSubjects && <SubSubjectAccordionContent subSubjects={Object.entries(mainSubject.children).sort((a, b) => {
        return a[1].order - b[1].order;
      }).map(([subSubjectGroupId, subSubject]) => ({
        name: subSubject.name,
        groupId: subSubjectGroupId,
        subjectGroupId: mainSubjectGroupId
      }))} />}
      </AccordionItem>;
  });
};
const SubSubjectAccordionContent = ({
  subSubjects
}: {
  subSubjects: {
    name: string;
    groupId: string;
    subjectGroupId: string;
  }[];
}) => {
  const {
    selectedSubject
  } = usePublicCategoryFilter();
  const {
    selectSubjectFilter
  } = usePublicSubjectFilterSelection();
  const getSelectedStatusColor = (selected: boolean): ClassValue[] => {
    return selected ? ['text-strong hover:text-violet-400', 'font-bold'] : ['hover:text-alternative'];
  };
  return <AccordionContent className={'flex flex-col gap-none bg-neutral px-none py-md'} data-sentry-element="AccordionContent" data-sentry-component="SubSubjectAccordionContent" data-sentry-source-file="SubjectAccordion.tsx">
      {subSubjects.map(({
      groupId,
      name,
      subjectGroupId
    }) => <div onClick={() => {
      selectSubjectFilter({
        subjectId: groupId,
        subjectGroupId,
        shouldExpandSubjectGroup: true
      });
    }} className={cn('h-[32px] py-2xs flex gap-xs pl-xl items-center', 'text-body-md-medium cursor-pointer w-full', getSelectedStatusColor(groupId === selectedSubject))} data-id={groupId.slice(2)} key={groupId}>
          {name}
          <SubjectGroupCount categoryGroupId={groupId} isSubSubject={true} />
        </div>)}
    </AccordionContent>;
};
export const AccordionContentItemWithRadio = (props: ComponentPropsWithoutRef<'label'> & {
  value: string;
  name: string;
}) => {
  const {
    className,
    value,
    name,
    ...rest
  } = props;
  return <Label htmlFor={value} className={cn('flex cursor-pointer hover:text-strong  w-full items-center gap-xs', className)} {...rest} data-sentry-element="Label" data-sentry-component="AccordionContentItemWithRadio" data-sentry-source-file="SubjectAccordion.tsx">
      <RadioGroupItem value={value} id={value} data-sentry-element="RadioGroupItem" data-sentry-source-file="SubjectAccordion.tsx" />
      <span className={'w-full text-body-md-semibold'}>{name}</span>
    </Label>;
};
export const AccordionContentItemWithCheckbox = (props: ComponentPropsWithoutRef<'label'> & {
  checked: boolean;
  name: string;
  onCheckedChange: (checked: boolean) => void;
}) => {
  const {
    className,
    name,
    onCheckedChange,
    checked,
    id,
    ...rest
  } = props;
  return <Label htmlFor={id} className={cn('flex cursor-pointer  hover:text-strong  w-full items-center gap-xs', className)} {...rest} data-sentry-element="Label" data-sentry-component="AccordionContentItemWithCheckbox" data-sentry-source-file="SubjectAccordion.tsx">
      <Checkbox onCheckedChange={onCheckedChange} checked={checked} id={id} data-sentry-element="Checkbox" data-sentry-source-file="SubjectAccordion.tsx" />
      <span className={'w-full text-body-md-semibold '}>{name}</span>
    </Label>;
};